"use client";

/**
 * AudioReactiveCover — wraps cover art so it gently ZOOMS with the music.
 *
 * Reads the global `window.__bbAudioLevel` (0..1) that the VU strip broadcasts,
 * so it reacts to whatever the global media player is playing and works on every
 * platform — including iOS, where the strip feeds a deterministic pulse instead
 * of a real Web-Audio analyser (which iOS deliberately skips for lock-screen
 * playback). Visible to ALL users — no admin/creator gate.
 *
 * The parent must be `position:relative` + `overflow-hidden` so the scaled art
 * is clipped to the frame (a clean "breathing" zoom, never spilling out).
 * Honours `prefers-reduced-motion`.
 */

import React, { useEffect, useRef } from "react";

interface Props {
  children: React.ReactNode;
  /** Peak extra zoom at full loudness (0.06 = +6%). */
  maxScale?: number;
  className?: string;
}

export function AudioReactiveCover({ children, maxScale = 0.06, className = "" }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    let raf = 0;
    let smooth = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const el = ref.current;
      if (!el) return;
      const lvl = Math.max(0, Math.min(1, (window as any).__bbAudioLevel || 0));
      // Fast attack, slow release → musical breathing, not jitter.
      smooth += (lvl - smooth) * (lvl > smooth ? 0.35 : 0.08);
      const scale = 1 + smooth * maxScale;
      el.style.transform = `scale(${scale.toFixed(4)})`;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [maxScale]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ width: "100%", height: "100%", willChange: "transform", transformOrigin: "center" }}
    >
      {children}
    </div>
  );
}
